import React, { useState, useEffect } from 'react';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Eye, 
  Users, 
  User, 
  Crown, 
  Plus, 
  Trash2, 
  RotateCcw,
  AlertCircle,
  CheckCircle,
  X,
  Clock,
  HelpCircle,
  Circle,
  ChevronDown,
  Info
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import cacheBuster from '../utils/cache-buster';

// Helper function to check if a player has custom preferences or notes
const hasCustomPreferences = (player, userPreferences, currentUserRole) => {
  if (!player) return false; // Added this check to prevent errors
  
  // Only show preferences if user is authorized (admin/creator)
  if (currentUserRole !== 'admin' && currentUserRole !== 'creator') {
    return false;
  }
  
  // Check if player has user preferences set
  if (player.user_id && userPreferences[player.user_id]) {
    const prefs = userPreferences[player.user_id];
    if (prefs.preferredPlayers && prefs.preferredPlayers.length > 0 || 
        prefs.preferredStorytellers && prefs.preferredStorytellers.length > 0 || 
        (prefs.notes && prefs.notes.trim() !== '')) {
      return true;
    }
  }
  
  // Check if player has custom signup notes (not default system notes)
  if (player.notes && player.notes.trim() !== '') {
    const defaultNotes = [
      'Signed up via Discord',
      'Signed up via Discord button'
    ];
    const isCustomNote = !defaultNotes.includes(player.notes.trim());
    return isCustomNote;
  }
  
  return false;
};

// Helper function to detect mutual preferences for a player
const getMutualPreferences = (player, userPreferences, allPlayers) => {
  if (!player || !player.user_id || !userPreferences[player.user_id]) {
    return { mutualCount: 0, mutualPlayers: [] };
  }
  
  const playerPrefs = userPreferences[player.user_id];
  if (!playerPrefs.preferredPlayers || playerPrefs.preferredPlayers.length === 0) {
    return { mutualCount: 0, mutualPlayers: [] };
  }
  
  const mutualPlayers = [];
  let mutualCount = 0;
  
  // Check each preferred player to see if they also prefer this player
  playerPrefs.preferredPlayers.forEach(prefId => {
    const prefPlayer = allPlayers.find(p => p.user_id === parseInt(prefId));
    if (prefPlayer && userPreferences[prefPlayer.user_id]) {
      const prefPlayerPrefs = userPreferences[prefPlayer.user_id];
      if (prefPlayerPrefs.preferredPlayers && 
          prefPlayerPrefs.preferredPlayers.includes(player.user_id)) {
        mutualPlayers.push(prefPlayer);
        mutualCount++;
      }
    }
  });
  
  return { mutualCount, mutualPlayers };
};

// Helper function to get visual styling for mutual preferences
const getMutualPreferenceStyle = (mutualCount, showGlow = true) => {
  if (mutualCount === 0 || !showGlow) return '';
  
  // Base glow effect
  let style = 'shadow-lg ';
  
  if (mutualCount === 1) {
    style += 'shadow-purple-400/50 dark:shadow-purple-500/50 ring-1 ring-purple-300 dark:ring-purple-600';
  } else if (mutualCount === 2) {
    style += 'shadow-purple-500/60 dark:shadow-purple-400/60 ring-2 ring-purple-400 dark:ring-purple-500';
  } else if (mutualCount >= 3) {
    style += 'shadow-purple-600/70 dark:shadow-purple-300/70 ring-2 ring-purple-500 dark:ring-purple-400 animate-pulse';
  }
  
  return style;
};

// Helper function to get hover styling for mutual preference highlighting
const getMutualHoverStyle = (player, hoveredPlayer, userPreferences, allPlayers, showGlow = true, currentUserRole) => {
  if (!hoveredPlayer || !player || player.user_id === hoveredPlayer.user_id || !showGlow) return '';
  
  // Check if the hovered player has mutual preferences with this player
  const hoveredPlayerPrefs = userPreferences[hoveredPlayer.user_id];
  const currentPlayerPrefs = userPreferences[player.user_id];
  
  if (!hoveredPlayerPrefs || !currentPlayerPrefs) return '';
  
  const isMutual = hoveredPlayerPrefs.preferredPlayers?.includes(player.user_id) && 
                   currentPlayerPrefs.preferredPlayers?.includes(hoveredPlayer.user_id);
  
  // Enhanced debug logging with cache buster integration - ONLY for admins and creators
  if (process.env.NODE_ENV === 'development' && hoveredPlayer && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
    console.log('🎯 Hover Style Debug (Admin/Creator Only):', {
      playerId: player.user_id,
      playerName: player.username,
      hoveredPlayerId: hoveredPlayer.user_id,
      hoveredPlayerName: hoveredPlayer.username,
      isMutual,
      showGlow,
      hasHoveredPrefs: !!hoveredPlayerPrefs,
      hasCurrentPrefs: !!currentPlayerPrefs,
      cacheVersion: cacheBuster.version,
      cacheTimestamp: cacheBuster.timestamp
    });
  }
  
  if (isMutual) {
    // Use enhanced CSS classes for better cross-environment compatibility
    return 'preference-glow preference-hover ring-2 ring-purple-500 dark:ring-purple-400 shadow-lg shadow-purple-500/50 dark:shadow-purple-400/50 scale-105 transition-all duration-200 z-10';
  } else {
    return 'preference-fade opacity-40 grayscale transition-all duration-200';
  }
};

// Helper function to get mutual preference tooltip text
const getMutualPreferenceTooltip = (mutualPlayers, allPlayers, currentUserRole) => {
  if (mutualPlayers.length === 0) return '';
  
  const mutualNames = mutualPlayers.map(player => {
    const playerData = allPlayers.find(p => p.user_id === player.user_id);
    return playerData?.username || `User ${player.user_id}`;
  });
  
  const tooltipText = `Mutual with: ${mutualNames.join(', ')}`;
  
  // Enhanced debug logging with cache buster integration - ONLY for admins and creators
  if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
    console.log('💡 Tooltip Debug (Admin/Creator Only):', {
      mutualCount: mutualPlayers.length,
      mutualNames,
      tooltipText,
      cacheVersion: cacheBuster.version,
      cacheTimestamp: cacheBuster.timestamp
    });
  }
  
  return tooltipText;
};

// Sortable Player Component for Available Players
function SortablePlayer({ player, userPreferences, currentUserRole, onViewPreferences, storytellers, allPlayers, showMutualGlow, hoveredPlayer, onHoverPlayer }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `player-${player.user_id || player.id}`,
    data: {
      type: 'player',
      player: player
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasPrefs = hasCustomPreferences(player, userPreferences, currentUserRole);
  const isStoryteller = storytellers.some(s => s.user_id === (player.user_id || player.id));
  
  // Get mutual preferences
  const { mutualCount, mutualPlayers } = getMutualPreferences(player, userPreferences, allPlayers);
  const mutualStyle = getMutualPreferenceStyle(mutualCount, showMutualGlow);
  const hoverStyle = getMutualHoverStyle(player, hoveredPlayer, userPreferences, allPlayers, showMutualGlow, currentUserRole);
  
  // Check if user can manage groups
  const canManageGroups = currentUserRole === 'admin' || currentUserRole === 'creator';
  
  // Debug logging for storyteller detection
  // console.log('🔍 SortablePlayer - Checking storyteller status:', {
  //   playerId: player.user_id || player.id,
  //   playerUsername: player.username,
  //   storytellers: storytellers.map(s => ({ user_id: s.user_id, username: s.username })),
  //   isStoryteller
  // });

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return { icon: <CheckCircle size={14} />, color: 'text-green-600 dark:text-green-400' };
      case 'late':
        return { icon: <Clock size={14} />, color: 'text-orange-600 dark:text-orange-400' };
      case 'tentative':
        return { icon: <HelpCircle size={14} />, color: 'text-yellow-600 dark:text-yellow-400' };
      default:
        return { icon: <Circle size={14} />, color: 'text-gray-500 dark:text-gray-400' };
    }
  };

  const statusInfo = getStatusIcon(player.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => onHoverPlayer && onHoverPlayer(player)}
      onMouseLeave={() => onHoverPlayer && onHoverPlayer(null)}
      className={`flex items-center justify-between rounded p-2 ${canManageGroups ? 'cursor-move' : ''} hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
        isStoryteller 
          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 shadow-sm' 
          : 'bg-gray-50 dark:bg-gray-700'
      } ${mutualStyle} ${hoverStyle}`}
    >
      <div className="flex items-center space-x-2">
        {player.avatar && player.discord_id ? (
          <img 
            src={`https://cdn.discordapp.com/avatars/${player.discord_id}/${player.avatar}.png`}
            alt={`${player.username}'s avatar`}
            className={`w-6 h-6 rounded-full object-cover ${
              isStoryteller ? 'ring-2 ring-amber-400 dark:ring-amber-500' : ''
            }`}
          />
        ) : (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            isStoryteller 
              ? 'bg-amber-200 dark:bg-amber-700 ring-2 ring-amber-400 dark:ring-amber-500' 
              : 'bg-gray-300 dark:bg-gray-600'
          }`}>
            <User size={12} className={isStoryteller ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'} />
          </div>
        )}
        <div className="flex items-center space-x-2">
          <span className={`text-sm font-medium ${
            isStoryteller ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-gray-100'
          }`}>
            {player.username}
            {isStoryteller && (
              <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">(Storyteller)</span>
            )}
          </span>
          <div className={`${statusInfo.color}`}>
            {statusInfo.icon}
          </div>
        </div>
        {hasPrefs && canManageGroups && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onViewPreferences(player.user_id || player.id);
            }}
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1"
            title="View preferences"
          >
            <Eye size={14} />
          </button>
        )}
        {mutualCount > 0 && (
          <div 
            className="flex items-center space-x-1 cursor-help"
            title={getMutualPreferenceTooltip(mutualPlayers, allPlayers, currentUserRole)}
          >
            <div className={`w-2 h-2 rounded-full ${
              mutualCount === 1 ? 'bg-purple-400 dark:bg-purple-500' :
              mutualCount === 2 ? 'bg-purple-500 dark:bg-purple-400' :
              'bg-purple-600 dark:bg-purple-300'
            }`} />
            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              {mutualCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Sortable Player Component for Players in Groups
function SortableGroupPlayer({ player, groupId, onRemovePlayer, userPreferences, currentUserRole, onViewPreferences, allPlayers, showMutualGlow, hoveredPlayer, onHoverPlayer }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `group-player-${groupId}-${player.user_id || player.id}`,
    data: {
      type: 'group-player',
      player: player,
      groupId: groupId
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasPrefs = hasCustomPreferences(player, userPreferences, currentUserRole);
  
  // Get mutual preferences
  const { mutualCount, mutualPlayers } = getMutualPreferences(player, userPreferences, allPlayers);
  const mutualStyle = getMutualPreferenceStyle(mutualCount, showMutualGlow);
  const hoverStyle = getMutualHoverStyle(player, hoveredPlayer, userPreferences, allPlayers, showMutualGlow, currentUserRole);

  const handleButtonClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  // Check if user can manage groups
  const canManageGroups = currentUserRole === 'admin' || currentUserRole === 'creator';

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return { icon: <CheckCircle size={12} />, color: 'text-green-600 dark:text-green-400' };
      case 'late':
        return { icon: <Clock size={12} />, color: 'text-orange-600 dark:text-orange-400' };
      case 'tentative':
        return { icon: <HelpCircle size={12} />, color: 'text-yellow-600 dark:text-yellow-400' };
      default:
        return { icon: <Circle size={12} />, color: 'text-gray-500 dark:text-gray-400' };
    }
  };

  const statusInfo = getStatusIcon(player.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => onHoverPlayer && onHoverPlayer(player)}
      onMouseLeave={() => onHoverPlayer && onHoverPlayer(null)}
      className={`flex items-center justify-between rounded p-2 ${canManageGroups ? 'cursor-move' : ''} hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
        player.status?.toLowerCase() === 'late' ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700' :
        player.status?.toLowerCase() === 'tentative' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700' :
        'bg-gray-50 dark:bg-gray-700'
      } ${mutualStyle} ${hoverStyle}`}
    >
      <div className="flex items-center space-x-2">
        {player.avatar && player.discord_id ? (
          <img 
            src={`https://cdn.discordapp.com/avatars/${player.discord_id}/${player.avatar}.png`}
            alt={`${player.username}'s avatar`}
            className="w-5 h-5 rounded-full object-cover"
          />
        ) : (
          <div className="w-5 h-5 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
            <User size={10} className="text-gray-500 dark:text-gray-400" />
          </div>
        )}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {player.username || 'Unknown Player'}
          </span>
          <div className={`${statusInfo.color}`}>
            {statusInfo.icon}
          </div>
          {player.status?.toLowerCase() !== 'confirmed' && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              player.status?.toLowerCase() === 'late' ? 'bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-300' :
              player.status?.toLowerCase() === 'tentative' ? 'bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300' :
              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}>
              {player.status}
            </span>
          )}
        </div>
        {hasPrefs && canManageGroups && (
          <button
            onClick={(e) => handleButtonClick(e, () => onViewPreferences(player.user_id || player.id))}
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1"
            title="View preferences"
          >
            <Eye size={14} />
          </button>
        )}
        {mutualCount > 0 && (
          <div 
            className="flex items-center space-x-1 cursor-help"
            title={getMutualPreferenceTooltip(mutualPlayers, allPlayers, currentUserRole)}
          >
            <div className={`w-2 h-2 rounded-full ${
              mutualCount === 1 ? 'bg-purple-400 dark:bg-purple-500' :
              mutualCount === 2 ? 'bg-purple-500 dark:bg-purple-400' :
              'bg-purple-600 dark:bg-purple-300'
            }`} />
            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              {mutualCount}
            </span>
          </div>
        )}
      </div>
      {canManageGroups && (
        <button
          onClick={(e) => handleButtonClick(e, () => onRemovePlayer(groupId, player.user_id || player.id))}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1"
          title="Remove player"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// Droppable Group Component
function DroppableGroup({ group, groups, onRemovePlayer, onAddPlayer, onRemoveGroup, onUpdateGroup, availablePlayers, userPreferences, currentUserRole, storytellers, onAssignStoryteller, getUserNameById, getStorytellerNameById, confirmedSignups, onViewPreferences, showMutualGlow, hoveredPlayer, onHoverPlayer }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: group.id,
    data: {
      type: 'group',
      group: group
    }
  });

  const {
    setNodeRef: setDroppableRef,
    isOver,
  } = useDroppable({
    id: `droppable-${group.id}`,
    data: {
      type: 'group',
      groupId: group.id
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [showPreferences, setShowPreferences] = useState({});

  const togglePreferences = (playerId) => {
    setShowPreferences(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  const getPlayerInfo = (playerId) => {
    // First try to find in availablePlayers, then in all confirmedSignups
    let player = availablePlayers.find(p => (p.user_id || p.id) === playerId);
    if (!player && confirmedSignups) {
      // If not found in availablePlayers, search in all confirmedSignups
      player = confirmedSignups.find(p => (p.user_id || p.id) === playerId);
    }
    return player;
  };

  const getPlayerPreferences = (playerId) => {
    const player = getPlayerInfo(playerId);
    
    if (!player) {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    if (!player.user_id) {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    // Only return preferences if user is authorized (admin/creator)
    if (currentUserRole !== 'admin' && currentUserRole !== 'creator') {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    const prefs = userPreferences[player.user_id];
    
    return prefs || { preferredPlayers: [], preferredStorytellers: [], notes: '' };
  };

  const hasPreferences = (playerId) => {
    const prefs = getPlayerPreferences(playerId);
    return prefs.preferredPlayers.length > 0 || 
           prefs.preferredStorytellers.length > 0 || 
           prefs.notes.trim() !== '';
  };

  const handleButtonClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  const handleStorytellerChange = (e) => {
    e.stopPropagation();
    const storytellerId = e.target.value;
    onAssignStoryteller(group.id, storytellerId || null);
  };

  const openPreferencesModal = (playerId) => {
    togglePreferences(playerId);
  };

  // Check if user can manage groups
  const canManageGroups = currentUserRole === 'admin' || currentUserRole === 'creator';

  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${
        isOver ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
    >
      <div 
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`flex items-center justify-between mb-3 ${canManageGroups ? 'cursor-move' : ''}`}
      >
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{group.name}</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({group.players ? group.players.length : 0} players)
            {group.storytellerId && ' + 1 storyteller'}
          </span>
        </div>
        {canManageGroups && (
          <button
            onClick={(e) => handleButtonClick(e, () => onRemoveGroup(group.id))}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1"
            title="Remove group"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Storyteller Assignment - Only for creators/admins */}
      {canManageGroups && storytellers.length > 0 && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Assign Storyteller:
          </label>
          <select
            value={group.storytellerId || ''}
            onChange={handleStorytellerChange}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            <option key="no-storyteller" value="">No storyteller assigned</option>
            {storytellers.map((storyteller) => {
              // Check if this storyteller is assigned to another group
              const isAssignedToOtherGroup = groups.some(g => 
                g.id !== group.id && g.storytellerId === storyteller.user_id
              );
              
              return (
                <option 
                  key={storyteller.user_id} 
                  value={storyteller.user_id}
                  disabled={isAssignedToOtherGroup}
                >
                  {storyteller.username}
                  {isAssignedToOtherGroup ? ' (Assigned to another group)' : ''}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Droppable area for players */}
      <div 
        ref={setDroppableRef}
        className={`min-h-[100px] border-2 border-dashed rounded-lg p-2 transition-colors ${
          isOver 
            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        {/* Storyteller at the top (if assigned) */}
        {group.storytellerId && (
          <div className="mb-3">
            <div className="flex items-center space-x-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <Crown size={16} className="text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Storyteller:</span>
              <span className="text-sm text-amber-800 dark:text-amber-200">
                {getStorytellerNameById(group.storytellerId)}
              </span>
            </div>
          </div>
        )}
        
        {/* Players in this group */}
        <div className="space-y-2">
          {group.players && group.players.length > 0 ? (
            (() => {
              // Debug logging to check for duplicate player IDs within this group
              const duplicatePlayerIds = group.players.filter((id, index) => group.players.indexOf(id) !== index);
              if (duplicatePlayerIds.length > 0) {
                console.warn(`⚠️ Duplicate player IDs in group ${group.id}:`, duplicatePlayerIds);
              }
              
              return group.players.map((playerId) => {
                const player = getPlayerInfo(playerId);
                if (!player) return null;
               
                return (
                  <SortableGroupPlayer
                    key={`${group.id}-${playerId}`}
                    player={player}
                    groupId={group.id}
                    onRemovePlayer={onRemovePlayer}
                    userPreferences={userPreferences}
                    currentUserRole={currentUserRole}
                    onViewPreferences={onViewPreferences}
                    allPlayers={confirmedSignups}
                    showMutualGlow={showMutualGlow}
                    hoveredPlayer={hoveredPlayer}
                    onHoverPlayer={onHoverPlayer}
                  />
                );
              });
            })()
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              {isOver ? 'Drop player here' : 'No players assigned'}
            </div>
          )}
        </div>
      </div>

             {/* Preferences Modal - REMOVED - Will be moved to main EventGroups component */}
    </div>
  );
}

const EventGroups = ({ eventId, eventTitle }) => {
  const [groups, setGroups] = useState([]);
  const [confirmedSignups, setConfirmedSignups] = useState([]);
  const [storytellers, setStorytellers] = useState([]);
  const [userPreferences, setUserPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoSplitConfig, setAutoSplitConfig] = useState({
    triggerCount: 16,
    maxGroups: 2,
    minPlayersPerGroup: 8,
    maxPlayersPerGroup: 15
  });
  const [activeId, setActiveId] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [showPreferences, setShowPreferences] = useState({});
  const [expandedStatuses, setExpandedStatuses] = useState(['confirmed', 'late', 'tentative', 'other']);
  const [showMutualGlow, setShowMutualGlow] = useState(true); // Toggle for mutual preference glow
  const [hoveredPlayer, setHoveredPlayer] = useState(null); // Track which player is being hovered

  const { isDark } = useTheme();
  const { user } = useAuth();

  // Check if user can manage groups (creator or admin only)
  const canManageGroups = user?.role === 'admin' || user?.role === 'creator';
  const currentUserRole = user?.role;

  // Debug logging for hover and glow functionality - ONLY for admins and creators
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
      console.log('🎯 EventGroups - Hover/Glow Debug (Admin/Creator Only):', {
        showMutualGlow,
        hoveredPlayer: hoveredPlayer?.username || hoveredPlayer?.user_id,
        userPreferencesCount: Object.keys(userPreferences).length,
        canManageGroups
      });
    }
  }, [showMutualGlow, hoveredPlayer, userPreferences, canManageGroups, currentUserRole]);

  // Cache buster integration for preferences debugging - ONLY for admins and creators
  useEffect(() => {
    // Debug preferences functionality with cache buster - only for admins/creators
    cacheBuster.debugPreferences(userPreferences, showMutualGlow, hoveredPlayer, currentUserRole);
    
    // Test hover effects in development - only for admins/creators
    if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
      const hoverTest = cacheBuster.testHoverEffects(currentUserRole);
      console.log('🧪 Hover Effects Test (Admin/Creator Only):', hoverTest);
    }
  }, [userPreferences, showMutualGlow, hoveredPlayer, currentUserRole]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before activating drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/events/${eventId}/groups`);
      
      // Debug logging to identify data issues - ONLY for admins and creators
      if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
        console.log('🔍 Fetched Groups Data (Admin/Creator Only):', {
          groups: response.data.groups || [],
          confirmedSignups: response.data.confirmedSignups || [],
          storytellers: response.data.storytellers || []
        });
      }
      
      setGroups(response.data.groups || []);
      setConfirmedSignups(response.data.confirmedSignups || []);
      setStorytellers(response.data.storytellers || []);
      setUserPreferences(response.data.userPreferences || {});
      setAutoSplitConfig(response.data.autoSplitConfig || autoSplitConfig);
      

    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to load event groups');
    } finally {
      setLoading(false);
    }
  };

  const saveGroups = async (updatedGroups) => {
    try {
      await axios.post(`/api/events/${eventId}/groups`, { groups: updatedGroups });
      setGroups(updatedGroups);
      setPendingChanges(false);
    } catch (error) {
      console.error('Error saving groups:', error);
      setError('Failed to save groups');
    }
  };

  const performAutoSplit = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/events/${eventId}/groups/auto-split`);
      setGroups(response.data.groups);
      setError(null);
    } catch (error) {
      console.error('Error performing auto-split:', error);
      setError(error.response?.data?.error || 'Failed to perform auto-split');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    // Only allow drag operations if user can manage groups
    if (!canManageGroups) {
      setActiveId(null);
      return;
    }

    const { active, over } = event;
    
    if (active && over) {
      const activeId = active.id;
      const overId = over.id;
      
      // Debug logging to help identify duplicate key issues - ONLY for admins and creators
      if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
        console.log('🔍 Drag End Debug (Admin/Creator Only):', {
          activeId,
          overId,
          activeData: active.data?.current,
          overData: over.data?.current
        });
      }
      
      // Handle group reordering
      if (active.data?.current?.type === 'group' && over.data?.current?.type === 'group') {
        if (activeId !== overId) {
          const updatedGroups = arrayMove(groups, 
            groups.findIndex(item => item.id === activeId),
            groups.findIndex(item => item.id === overId)
          );
          setGroups(updatedGroups);
          setPendingChanges(true);
        }
      }
      
      // Handle player assignment from available players to group
      if (active.data?.current?.type === 'player' && over.data?.current?.type === 'group') {
        const playerId = active.data.current.player.user_id || active.data.current.player.id;
        const groupId = over.data.current.groupId;
        
        // Remove player from any existing group
        const updatedGroups = groups.map(group => ({
          ...group,
          players: (group.players || []).filter(p => p !== playerId)
        }));
        
        // Add player to the target group
        const finalGroups = updatedGroups.map(group => {
          if (group.id == groupId) { // Use == for type coercion
            return {
              ...group,
              players: [...(group.players || []), playerId]
            };
          }
          return group;
        });
        
        setGroups(finalGroups);
        setPendingChanges(true);
      }
      
      // Handle player movement between groups
      if (active.data?.current?.type === 'group-player' && (over.data?.current?.type === 'group' || over.data?.current?.type === 'group-player')) {
        const playerId = active.data.current.player.user_id || active.data.current.player.id;
        const sourceGroupId = active.data.current.groupId;
        const targetGroupId = over.data?.current?.type === 'group' ? over.data.current.groupId : over.data.current.groupId;
        
        if (sourceGroupId !== targetGroupId) {
          const updatedGroups = groups.map(group => {
            if (group.id == sourceGroupId) { // Use == for type coercion
              return {
                ...group,
                players: (group.players || []).filter(p => p !== playerId)
              };
            } else if (group.id == targetGroupId) { // Use == for type coercion
              return {
                ...group,
                players: [...(group.players || []), playerId]
              };
            }
            return group;
          });
          
          setGroups(updatedGroups);
          setPendingChanges(true);
        }
      }
    }
    
    setActiveId(null);
  };

  const addGroup = () => {
    if (!canManageGroups) return;
    
    const newGroup = {
      id: `group-${Date.now()}`,
      name: `Group ${groups.length + 1}`,
      players: [],
      storytellerId: null,
      maxPlayers: 15,
      createdAt: new Date().toISOString()
    };
    const updatedGroups = [...groups, newGroup];
    setGroups(updatedGroups);
    setPendingChanges(true);
  };

  const removeGroup = (groupId) => {
    if (!canManageGroups) return;
    
    const updatedGroups = groups.filter(g => g.id !== groupId);
    setGroups(updatedGroups);
    setPendingChanges(true);
  };

  const addPlayerToGroup = (groupId, playerId) => {
    if (!canManageGroups) return;
    
    const updatedGroups = groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          players: [...(group.players || []), playerId]
        };
      }
      return group;
    });
    setGroups(updatedGroups);
    setPendingChanges(true);
  };

  const removePlayerFromGroup = (groupId, playerId) => {
    if (!canManageGroups) return;
    
    const updatedGroups = groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          players: (group.players || []).filter(p => p !== playerId)
        };
      }
      return group;
    });
    setGroups(updatedGroups);
    setPendingChanges(true);
  };

  const assignStorytellerToGroup = (groupId, storytellerId) => {
    if (!canManageGroups) return;
    
    // Convert storytellerId to number for consistency, but handle empty/null values
    const storytellerIdNum = storytellerId && storytellerId !== '' ? parseInt(storytellerId) : null;
    
    // If assigning a storyteller, remove them from all groups and available players
    let updatedGroups = groups.map(group => {
      let updatedGroup = { ...group };
      
      if (group.id === groupId) {
        // Assign the storyteller to this group (or clear if storytellerIdNum is null)
        
        // Remove from player list if they were assigned as a player in this group
        if (storytellerIdNum && group.players && group.players.includes(storytellerIdNum)) {
          updatedGroup.players = group.players.filter(p => p !== storytellerIdNum);
        }
        
        // Set as storyteller (or clear if null)
        updatedGroup.storytellerId = storytellerIdNum;
      } else {
        // Remove this storyteller from other groups (both as storyteller and as player)
        
        // Clear storyteller assignment from other groups
        if (storytellerIdNum && group.storytellerId === storytellerIdNum) {
          updatedGroup.storytellerId = null;
        }
        
        // Remove from player list if they were assigned as a player
        if (storytellerIdNum && group.players && group.players.includes(storytellerIdNum)) {
          updatedGroup.players = group.players.filter(p => p !== storytellerIdNum);
        }
      }
      
      return updatedGroup;
    });
    
    setGroups(updatedGroups);
    setPendingChanges(true);
    
    // Force a re-render to update available players
    setTimeout(() => {
    }, 100);
  };

  const savePendingChanges = () => {
    if (!canManageGroups || !pendingChanges) return;
    
    saveGroups(groups);
  };

  // Preferences modal functions
  const togglePreferences = (playerId) => {
    setShowPreferences(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  const getPlayerInfo = (playerId) => {
    // Convert playerId to number for consistent comparison
    const playerIdNum = parseInt(playerId);
    
    // Search in all confirmedSignups - check both user_id and id fields
    const player = confirmedSignups.find(signup => 
      (signup.user_id === playerIdNum) || 
      (signup.id === playerIdNum) ||
      (signup.user_id === playerId) || 
      (signup.id === playerId)
    );
    
    return player;
  };

  const getPlayerPreferences = (playerId) => {
    const player = getPlayerInfo(playerId);
    
    if (!player) {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    if (!player.user_id) {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    // Only return preferences if user is authorized (admin/creator)
    if (!canManageGroups) {
      return { preferredPlayers: [], preferredStorytellers: [], notes: '' };
    }
    
    const prefs = userPreferences[player.user_id];
    
    return prefs || { preferredPlayers: [], preferredStorytellers: [], notes: '' };
  };

  const openPreferencesModal = (playerId) => {
    togglePreferences(playerId);
  };

  // Cache buster force reload function for debugging - ONLY for admins and creators
  const handleCacheBusterReload = () => {
    if (process.env.NODE_ENV === 'development' && (currentUserRole === 'admin' || currentUserRole === 'creator')) {
      console.log('🔄 Force reloading resources for cache busting (Admin/Creator Only)...');
      cacheBuster.forceReload();
      
      // Test hover effects after reload - only for admins/creators
      const hoverTest = cacheBuster.testHoverEffects(currentUserRole);
      console.log('🧪 Post-reload hover test (Admin/Creator Only):', hoverTest);
    }
  };

  // Helper functions to resolve user IDs to usernames
  const getUserNameById = (userId) => {
    const user = confirmedSignups.find(signup => (signup.user_id || signup.id) === userId);
    return user?.username || 'Unknown User';
  };

  const getStorytellerNameById = (storytellerId) => {
    const storyteller = storytellers.find(s => s.user_id === storytellerId);
    return storyteller?.username || 'Unknown Storyteller';
  };

  // Check if we have enough confirmed players (not assigned as storytellers) for auto-split
  const confirmedPlayersNotStorytellers = confirmedSignups.filter(signup => {
    const playerId = signup.user_id || signup.id;
    const isAssignedAsStoryteller = groups.some(group => 
      group.storytellerId === playerId
    );
    return signup.status?.toLowerCase() === 'confirmed' && !isAssignedAsStoryteller;
  });
  
  const shouldShowAutoSplit = confirmedPlayersNotStorytellers.length >= 16 && canManageGroups;

  // Get available players (not assigned to any group and not assigned as storytellers)
  // Show all players regardless of status, but only confirmed players count for auto-split
  const availablePlayers = confirmedSignups.filter(signup => {
    const playerId = signup.user_id || signup.id;
    const isAssignedToGroup = groups.some(group => 
      group.players && group.players.includes(playerId)
    );
    const isAssignedAsStoryteller = groups.some(group => 
      group.storytellerId === playerId
    );
    
    // Check if player is a storyteller (for logging purposes)
    const isStoryteller = storytellers.some(s => s.user_id === playerId);
    
    // Include players who are NOT assigned to groups and NOT assigned as storytellers
    // Storytellers should not appear in available players if they're assigned
    const shouldInclude = !isAssignedToGroup && !isAssignedAsStoryteller;
    
    if (isStoryteller && !shouldInclude) {
      
    }
    
    return shouldInclude;
  });
  
  // Debug logging for available players result
  

  useEffect(() => {
    fetchGroups();
  }, [eventId]);

  // Debug state changes
  useEffect(() => {

  }, [confirmedSignups, storytellers, userPreferences]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Event Groups</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {confirmedSignups.length} confirmed players
            {pendingChanges && canManageGroups && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">• Unsaved changes</span>
            )}
          </p>
        </div>
        
        {canManageGroups && (
          <div className="flex space-x-2">
            {shouldShowAutoSplit && (
              <button
                onClick={performAutoSplit}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 dark:bg-blue-600 dark:hover:bg-blue-700 dark:disabled:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <RotateCcw size={16} />
                <span>Auto-Split</span>
              </button>
            )}
            
            <button
              onClick={addGroup}
              className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={16} />
              <span>Add Group</span>
            </button>

            <button
              onClick={() => setShowMutualGlow(!showMutualGlow)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showMutualGlow 
                  ? 'bg-purple-500 hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700 text-white' 
                  : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              title="Toggle mutual preference glow and hover effects"
            >
              <div className={`w-3 h-3 rounded-full ${
                showMutualGlow ? 'bg-white' : 'bg-gray-500'
              }`} />
              <span>Glow & Hover Effects</span>
            </button>

            {pendingChanges && (
              <button
                onClick={savePendingChanges}
                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <CheckCircle size={16} />
                <span>Save Changes</span>
              </button>
            )}

            {/* Debug button for cache buster - only in development */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={handleCacheBusterReload}
                className="flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
                title="Force reload resources for cache busting (dev only)"
              >
                <RotateCcw size={16} />
                <span>Cache Buster</span>
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-2">
          <AlertCircle size={16} className="text-red-500 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
        </div>
      )}

      {/* Only show group management info for creators/admins */}
      {canManageGroups && groups.length > 0 && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2">
          <Info size={16} className="text-green-500 dark:text-green-400" />
          <span className="text-green-700 dark:text-green-300 text-sm">
            <strong>Group Management:</strong> You can drag players between groups, unbalance groups, or reassign storytellers as needed. Auto-split is just a starting point!
          </span>
        </div>
      )}

      {/* Only show auto-split info for creators/admins */}
      {shouldShowAutoSplit && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center space-x-2">
          <CheckCircle size={16} className="text-blue-500 dark:text-blue-400" />
          <span className="text-blue-700 dark:text-blue-300 text-sm">
            Auto-split available! You have {confirmedPlayersNotStorytellers.length} confirmed players (minimum 16 required). Will create 2 balanced groups of 8+ players each. <strong>Note: Auto-split is a suggestion - you can freely move players between groups after splitting.</strong>
          </span>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={(() => {
            const groupIds = groups.map(g => g.id);
            const availablePlayerIds = availablePlayers.map(p => `player-${p.user_id || p.id}`);
            const groupPlayerIds = groups.flatMap(g => (g.players || []).map(p => `group-player-${g.id}-${p}`));
            
            const allItems = [...groupIds, ...availablePlayerIds, ...groupPlayerIds];
            
            // Debug logging to identify duplicate keys
            const duplicates = allItems.filter((item, index) => allItems.indexOf(item) !== index);
            if (duplicates.length > 0) {
              console.warn('⚠️ Duplicate keys detected:', duplicates);
            }
            
            return allItems;
          })()}
          strategy={verticalListSortingStrategy}
        >
          {/* Groups Section */}
          {groups.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Groups</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  // Debug logging to check for duplicate group IDs
                  const groupIds = groups.map(g => g.id);
                  const duplicateGroupIds = groupIds.filter((id, index) => groupIds.indexOf(id) !== index);
                  if (duplicateGroupIds.length > 0) {
                    console.warn('⚠️ Duplicate group IDs detected:', duplicateGroupIds);
                  }
                  
                  return groups.map((group) => (
                    <DroppableGroup
                      key={group.id}
                      group={group}
                      groups={groups}
                      onRemovePlayer={removePlayerFromGroup}
                      onAddPlayer={addPlayerToGroup}
                      onRemoveGroup={removeGroup}
                      onUpdateGroup={() => {}}
                      onAssignStoryteller={assignStorytellerToGroup}
                      availablePlayers={availablePlayers}
                      userPreferences={userPreferences}
                      currentUserRole={user?.role}
                      storytellers={storytellers}
                      getUserNameById={getUserNameById}
                      getStorytellerNameById={getStorytellerNameById}
                      confirmedSignups={confirmedSignups}
                      onViewPreferences={openPreferencesModal}
                      showMutualGlow={showMutualGlow}
                      hoveredPlayer={hoveredPlayer}
                      onHoverPlayer={setHoveredPlayer}
                    />
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Available Players Section - Only visible for creators/admins */}
          {canManageGroups && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Available Players</h3>
              
              {/* Group players by status */}
              {(() => {
                const playersByStatus = {
                  confirmed: availablePlayers.filter(p => p.status?.toLowerCase() === 'confirmed'),
                  late: availablePlayers.filter(p => p.status?.toLowerCase() === 'late'),
                  tentative: availablePlayers.filter(p => p.status?.toLowerCase() === 'tentative'),
                  other: availablePlayers.filter(p => !p.status || !['confirmed', 'late', 'tentative'].includes(p.status.toLowerCase()))
                };

                const statusConfig = [
                  { key: 'confirmed', label: 'Confirmed', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-700' },
                  { key: 'late', label: 'Late', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20', borderColor: 'border-orange-200 dark:border-orange-700' },
                  { key: 'tentative', label: 'Tentative', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', borderColor: 'border-yellow-200 dark:border-yellow-700' },
                  { key: 'other', label: 'Other', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-50 dark:bg-gray-900/20', borderColor: 'border-gray-200 dark:border-gray-700' }
                ];

                return (
                  <div className="space-y-3">
                    {statusConfig.map(({ key, label, color, bgColor, borderColor }) => {
                      const players = playersByStatus[key];
                      if (players.length === 0) return null;

                      const isExpanded = expandedStatuses.includes(key);
                      const statusIcon = key === 'confirmed' ? <CheckCircle size={16} /> :
                                       key === 'late' ? <Clock size={16} /> :
                                       key === 'tentative' ? <HelpCircle size={16} /> :
                                       <Circle size={16} />;

                      return (
                        <div key={key} className={`border rounded-lg ${borderColor} ${bgColor}`}>
                          <button
                            onClick={() => setExpandedStatuses(prev => 
                              prev.includes(key) 
                                ? prev.filter(s => s !== key)
                                : [...prev, key]
                            )}
                            className={`w-full px-4 py-3 flex items-center justify-between text-left ${color} hover:opacity-80 transition-opacity`}
                          >
                            <div className="flex items-center space-x-2">
                              {statusIcon}
                              <span className="font-medium">{label}</span>
                              <span className="text-sm opacity-75">({players.length})</span>
                            </div>
                            <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                              <ChevronDown size={16} />
                            </div>
                          </button>
                          
                          {isExpanded && (
                            <div className="px-4 pb-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {players.map((signup) => (
                                  <SortablePlayer
                                    key={signup.user_id || signup.id}
                                    player={signup}
                                    userPreferences={userPreferences}
                                    currentUserRole={user?.role}
                                    onViewPreferences={openPreferencesModal}
                                    storytellers={storytellers}
                                    allPlayers={confirmedSignups}
                                    showMutualGlow={showMutualGlow}
                                    hoveredPlayer={hoveredPlayer}
                                    onHoverPlayer={setHoveredPlayer}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              
              {availablePlayers.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                  All players have been assigned to groups
                </div>
              )}
            </div>
          )}

          <DragOverlay>
            {activeId ? (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-lg">
                <div className="flex items-center space-x-2">
                  <User size={16} className="text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {activeId.startsWith('player-') 
                      ? availablePlayers.find(p => `player-${p.user_id || p.id}` === activeId)?.username || 'Player'
                      : activeId.startsWith('group-player-')
                      ? (() => {
                          const match = activeId.match(/^group-player-(\d+)-(\d+)$/);
                          if (match) {
                            const [, groupId, playerId] = match;
                            return confirmedSignups.find(p => (p.user_id || p.id) == playerId)?.username || 'Player';
                          }
                          return 'Player';
                        })()
                      : 'Group'
                    }
                  </span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </SortableContext>
      </DndContext>

      {/* Preferences Modal for Available Players */}
      {Object.keys(showPreferences).map(playerId => {
        if (!showPreferences[playerId]) return null;
        
        const prefs = getPlayerPreferences(playerId);
        const player = getPlayerInfo(playerId);
        
        // Debug logging
        // console.log('🔍 Preferences Modal Debug:', {
        //   playerId,
        //   player,
        //   prefs,
        //   userPreferences: Object.keys(userPreferences)
        // });
        
        return (
          <div 
            key={playerId} 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => togglePreferences(playerId)}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {player?.username || 'Player'} Preferences
                </h3>
                <button
                  onClick={() => togglePreferences(playerId)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                {prefs.preferredStorytellers && prefs.preferredStorytellers.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Preferred Storytellers:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {prefs.preferredStorytellers.map(id => getStorytellerNameById(id)).join(', ')}
                    </div>
                  </div>
                )}
                
                {prefs.preferredPlayers && prefs.preferredPlayers.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Preferred Players:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {prefs.preferredPlayers.map(id => getUserNameById(id)).join(', ')}
                    </div>
                  </div>
                )}
                
                {prefs.notes && prefs.notes.trim() !== '' && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Profile Notes:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {prefs.notes}
                    </div>
                  </div>
                )}
                
                {player?.notes && player.notes.trim() !== '' && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Signup Notes:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {player.notes}
                    </div>
                  </div>
                )}
                
                {!hasCustomPreferences(player, userPreferences, user?.role) && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No custom preferences or notes set
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EventGroups;